import React, {useContext, useRef, useState} from 'react';

import CustomerTable from '../../views/customer/customer-table';
import CustomerForm from '../../views/customer/customer-form';
import {useIntl} from 'react-intl';
import useAlerts from '../../hooks/use-alerts';
import {ModalContext} from '../../apiContexts';
import useServices from '../../hooks/use-services';
import useCustomers from '../../hooks/use-customers';
import useUsers from '../../hooks/use-users';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {observer} from 'mobx-react';
import CustomerDeviceTable from '../../views/customer/customer-device-table';

const noCreator = {getUserById: () => undefined};

const formatCustomers = (arr, userStore = noCreator, timezone) => {
    return arr.map((item) => {
        const creator = userStore.getUserById(item.createdByUserId);
        return {
            ...item,
            creatorName: creator ? creator.name : '',
            creatorEmail: creator ? creator.email : '',
            // registrationDate é o campo real de "data de cadastro", escolhido pelo usuário no
            // formulário de cadastro, e existe para todo cliente. createdAt só existe a partir da
            // migração que o criou e é nulo nos registros antigos.
            createdAtFormatted: formatDateTimeColumn(item.registrationDate, timezone),
            updatedAtFormatted: formatDateTimeColumn(item.updatedAt, timezone),
        };
    });
};

const CustomerContainer = (props) => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {customers: customerStore} = useCustomers();
    const {customerService} = useServices();
    const {showModalLoading, isModalLoading} = useContext(ModalContext);
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();
    const [view, setView] = useState('customerList');
    const selectedCustomerData = useRef(null);

    const newRegister = async (data) => {
        try {
            data.registrationDate = new Date().toISOString();
            showModalLoading(true);
            const result = await customerService.save(data);
            customerStore.addCustomer(result);
            setView('customerList');
            toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const updateRegister = async (data, afterTo = 'customerList') => {
        try {
            showModalLoading(true);
            const result = await customerService.update(data);
            const {id: customerId, ...updatedCustomerData} = result;
            customerStore.updateCustomer(customerId, updatedCustomerData);
            setView(afterTo);
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
            selectedCustomerData.current = null;
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const removeRegister = async (data) => {
        try {
            showModalLoading(true);
            await customerService.remove(data.id);
            customerStore.removeCustomer(data.id);
            setView('customerList');
            toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const onPressDevices = (row) => {
        const customerId = row.id;
        customerStore.fetchCustomerDevices(customerId);
        setView('customerDeviceTable');
    };

    const onCustomerDeviceTableCancel = () => {
        setView('customerList');
        customerStore.cleanupCustomerDevices();
    };

    const onPressAdd = () => {
        setView('customerForm');
    };

    const onPressEdit = (data) => {
        selectedCustomerData.current = data;
        setView('customerForm');
    };

    const onCustomerFormCancel = () => {
        setView('customerList');
        selectedCustomerData.current = null;
    };

    return (
        <>
            {
                {
                    customerList: (
                        <CustomerTable
                            list={formatCustomers(customerStore.list, userStore, timezone)}
                            onPressAdd={onPressAdd}
                            onPressEdit={onPressEdit}
                            removeRegister={removeRegister}
                            showDeviceList={onPressDevices}
                        />
                    ),
                    customerForm: (
                        <CustomerForm
                            cancel={onCustomerFormCancel}
                            data={Object.assign({}, selectedCustomerData.current)}
                            edit={!!selectedCustomerData.current}
                            newRegister={newRegister}
                            updateRegister={updateRegister}
                            loading={isModalLoading}
                        />
                    ),
                    customerDeviceTable: (
                        <CustomerDeviceTable
                            onPressBack={onCustomerDeviceTableCancel}
                            list={customerStore.selectedCustomerDevices}
                        />
                    ),
                }[view]
            }
        </>
    );
};

export default observer(CustomerContainer);
